import React, { useMemo, useState } from 'react';
import Slide from '@material-ui/core/Slide/Slide';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import Dialog from '@material-ui/core/Dialog/Dialog';
import RoundedCloseIcon from '../../components/RoundedCloseIcon';
import { useModalStyles } from '../../../../theme/GivelifyModal';
import Label16 from '../../../../theme/labels/Label16';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { AppState } from '../../../../store';
import LocationAutoComplete, {
    isLocationValid,
} from '../../components/autocomplete/LocationAutoComplete';
import { useTranslation } from 'react-i18next';
import { noValidation } from '../../components/SimpleTextFieldComponent';
import SearchTextField from '../../components/SearchTextField';
import Button from '@material-ui/core/Button/Button';
import { signSetAction } from '../../../../store/signup/actions';
import { useButtonStyles } from '../../components/styles';
import { LocationSelected } from '../../../../store/signup/types';
import { styled } from '@mui/material';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children?: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Label = styled(Label16)(({ theme }) => ({
    padding: theme.spacing(2),
    borderBottom: '1px solid #E3E3E3',
}));

const StyledTextFields = styled('div')(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    margin: theme.spacing(2),
    '& .organization-name-field': {
        marginTop: 56,
        marginBottom: theme.spacing(1),
    },
    '& .button': {
        marginTop: theme.spacing(2),
    },
}));

interface OrganizationSearchModalProps {
    open: boolean;
    onClose: () => unknown;
}

const OrganizationSearchModal: React.FC<OrganizationSearchModalProps> = ({
    open,
    onClose,
}) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { submit, mainButton } = useButtonStyles();
    const { closeModal } = useModalStyles();
    const { organizationName, organizationLocation } = useSelector(
        (state: AppState) => ({
            organizationName: state.Signup.organizationName,
            organizationLocation: state.Signup.organizationLocation,
        }),
    );
    const { organizationSearchText, placeholder, nextLabelText } = useMemo(
        () => ({
            organizationSearchText: t(
                'signup.organizationSearch.organizationSearchText',
            ),
            placeholder: t('signup.organizationName.placeholders.search'),
            nextLabelText: t('labels.search'),
        }),
        [t],
    );
    const [name, setName] = useState<string>(organizationName);
    const [location, setLocation] =
        useState<LocationSelected>(organizationLocation);
    const onChangeName = (value: string) => {
        setName(value);
    };
    const onClick = () => {
        dispatch(
            signSetAction({
                organizationName: name,
                organizationLocation: location,
            }),
        );
        onClose();
    };
    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
        >
            <div className={closeModal} onClick={onClose}>
                <RoundedCloseIcon />
            </div>
            <Label>{organizationSearchText}</Label>
            <StyledTextFields>
                <SearchTextField
                    value={name}
                    type="text"
                    onChange={onChangeName}
                    placeholder={placeholder}
                    isFocus={undefined}
                    validation={noValidation}
                    maxLength={255}
                    className="organization-name-field"
                />
                <LocationAutoComplete
                    location={location}
                    onSubmit={setLocation}
                />
                <Button
                    className={`${submit} ${mainButton} button`}
                    variant="contained"
                    onClick={onClick}
                    disabled={name.length === 0 || !isLocationValid(location)}
                >
                    {nextLabelText}
                </Button>
            </StyledTextFields>
        </Dialog>
    );
};

export default OrganizationSearchModal;
